import React, { useCallback, useRef, useState } from "react";
import { IoIosSearch } from "react-icons/io";
import { LuCircleUserRound } from "react-icons/lu";

import { NavMenu } from "Components/molecules/NavMenu";
import { menuItems } from "shared/config/menuItemsConfig";
import { AUTH_SERVER_URL, startLogin } from "shared/auth/auth";
import { useAuth } from "shared/contexts/AuthContext";
import { useClickOutside } from "shared/hooks/useClickOutside";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";

import {
  HeaderAction,
  AccountMenu,
  AccountMenuButton,
  AccountMenuLink,
  AccountMenuWrapper,
  ButtonWrapper,
  HeaderWrapper,
  LoginAction,
  LogoWrapper,
  NavWrapper,
} from "./styles";

// @ts-ignore
import { ReactComponent as LogoVoopter } from "assets/svgs/logo-voopter.svg";

/**
 * Componente HeaderDesktop
 *
 * Exibe o cabeçalho com logotipo, menu de navegação e um banner.
 */
export const HeaderDesktop: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const closeAccountMenu = useCallback(() => setIsAccountMenuOpen(false), []);

  useClickOutside([accountMenuRef], closeAccountMenu);

  const handleSearchClick = () => {
    window.location.assign("/");
  };

  return (
    <HeaderWrapper>
      <LogoWrapper href="/">
        <LogoVoopter />
      </LogoWrapper>
      <NavWrapper>
        <NavMenu
          items={menuItems.map((item) => ({
            label: item.label,
            action: () => item.action(),
          }))}
        />
      </NavWrapper>
      <ButtonWrapper>
        <HeaderAction type="button" onClick={handleSearchClick}>
          <IoIosSearch aria-hidden="true" />
          Buscar passagem
        </HeaderAction>
        <AccountMenuWrapper ref={accountMenuRef}>
          <LoginAction
            href={isAuthenticated ? "/dados-pessoais" : AUTH_SERVER_URL}
            aria-expanded={isAuthenticated ? isAccountMenuOpen : undefined}
            aria-haspopup={isAuthenticated ? "menu" : undefined}
            onClick={(event) => {
              event.preventDefault();

              if (isAuthenticated) {
                setIsAccountMenuOpen((open) => {
                  const willOpen = !open;

                  if (willOpen) {
                    sendGTMEvent("accountMenuOpen", {
                      platform: getPlatformType(),
                      source: "header",
                      pagePath: window.location.pathname,
                    });
                  }

                  return willOpen;
                });
                return;
              }

              startLogin({ source: "header" });
            }}
          >
            <LuCircleUserRound aria-hidden="true" />
            {isAuthenticated ? "Minha Conta" : "Entrar"}
          </LoginAction>

          {isAuthenticated && isAccountMenuOpen && (
            <AccountMenu role="menu">
              <AccountMenuLink
                to="/dados-pessoais"
                role="menuitem"
                onClick={() => {
                  sendGTMEvent("accountAccess", {
                    platform: getPlatformType(),
                    source: "header_desktop",
                    section: "personal",
                  });
                  closeAccountMenu();
                }}
              >
                Dados Pessoais &amp; Acesso
              </AccountMenuLink>
              <AccountMenuLink
                to="/preferencias-notificacao"
                role="menuitem"
                onClick={() => {
                  sendGTMEvent("accountAccess", {
                    platform: getPlatformType(),
                    source: "header_desktop",
                    section: "notifications",
                  });
                  closeAccountMenu();
                }}
              >
                Preferências de Notificação
              </AccountMenuLink>
              <AccountMenuLink
                to="/privacidade"
                role="menuitem"
                onClick={() => {
                  sendGTMEvent("accountAccess", {
                    platform: getPlatformType(),
                    source: "header_desktop",
                    section: "privacy",
                  });
                  closeAccountMenu();
                }}
              >
                Privacidade e Encerramento de Conta
              </AccountMenuLink>
              <AccountMenuButton
                type="button"
                role="menuitem"
                onClick={() => {
                  void logout()
                    .then(() => {
                      sendGTMEvent("authLogout", {
                        platform: getPlatformType(),
                        source: "header_menu",
                        result: "success",
                      });
                      closeAccountMenu();
                      window.location.assign("/");
                    })
                    .catch(() => {
                      sendGTMEvent("authLogout", {
                        platform: getPlatformType(),
                        source: "header_menu",
                        result: "error",
                      });
                      window.alert("Não foi possível sair. Tente novamente.");
                    });
                }}
              >
                Sair
              </AccountMenuButton>
            </AccountMenu>
          )}
        </AccountMenuWrapper>
      </ButtonWrapper>
    </HeaderWrapper>
  );
};
